/**
 * ============================================================================
 * Department 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义部门管理模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createDepartmentApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数，传入适配器
 * import { createDepartmentApi } from '@bayes-mes/shared/api/department/factory'
 * export const departmentApi = createDepartmentApi(webAdapter)
 * 
 * // 3. 使用生成的 API
 * const result = await departmentApi.getDepartmentList({ page: 1 })
 * const tree = await departmentApi.getDepartmentTree()
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createDepartmentApi } from '@bayes-mes/shared/api/department/factory'
 * export const departmentApi = createDepartmentApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/department/factory
 * @description Department 模块 API 工厂函数
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-18
 * @last-updated 2025-10-18
 * 
 * @change-log
 * - 2025-10-18: 初始创建，实现所有基础 CRUD 方法和企业微信同步功能
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  Department,                        // 部门主实体类型
  DepartmentQueryParams,              // 列表查询参数
  CreateDepartmentForm,               // 创建表单
  UpdateDepartmentForm,               // 更新表单
  DepartmentStatistics,               // 统计数据
  WeworkSyncResult,                   // 企业微信同步结果
} from '../../types/department'

// ===== 导入通用类型 =====
import type { PaginationResult } from '../../types/api'

// ===== 导入统一的请求客户端接口 =====
// ⚠️ 重要：所有工厂函数都从这里导入接口，不要重复定义！
import type { IRequestClient } from '../core/client'

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes'

// ===== 导出工厂函数 =====
/**
 * 创建 Department 模块的 API 方法集合
 * 
 * 这是一个工厂函数，它接收一个"请求客户端"作为参数，
 * 返回一个包含所有部门相关 API 方法的对象。
 * 
 * @param client - 请求客户端，必须实现 IRequestClient 接口
 *   - 可以是 Web 端的 axios 适配器
 *   - 可以是 App 端的 uni.request 适配器
 *   - 可以是测试用的 Mock 客户端
 * 
 * @returns API 方法集合对象，包含所有部门相关的请求方法
 * 
 * 为什么这样设计？
 * - 解耦：API 逻辑与具体的请求实现分离
 * - 复用：同一套 API 定义可以在 Web 和 App 端使用
 * - 测试：可以注入 Mock 客户端进行单元测试
 */
export function createDepartmentApi(client: IRequestClient) {
  return {
    // ===== 基础 CRUD 操作 =====
    
    /**
     * 获取部门列表（分页）
     * 
     * 使用场景：
     * - 在部门管理页面的主列表中使用
     * - 支持分页、搜索、筛选功能
     * - 用户可以根据关键词、上级部门、状态等条件查询数据
     * 
     * @param params - 查询参数对象（可选）
     *   - keyword?: string - 关键词搜索（部门名称）
     *     示例：'研发'、'生产'、'财务'
     *   - parent_id?: number - 上级部门ID
     *     示例：1（查询ID为1的部门下的所有子部门）
     *   - status?: DepartmentStatus - 状态筛选
     *     示例：DepartmentStatus.ENABLED（只查询启用的部门）
     *   - include_disabled?: boolean - 是否包含禁用的部门
     *     示例：false（不包含禁用的）
     *   - page?: number - 页码，从 1 开始，默认 1
     *     示例：page: 1（第一页），page: 2（第二页）
     *   - per_page?: number - 每页数量，默认 15
     *     示例：per_page: 20（每页显示 20 条）
     *   - sort_by?: string - 排序字段
     *     示例：'order'（按排序值排序）、'created_at'（按创建时间排序）
     *   - sort_order?: 'asc' | 'desc' - 排序方式
     *     示例：'asc'（升序）、'desc'（降序）
     * 
     * @returns Promise<PaginationResult<Department>>
     *   - data: Department[] - 部门列表数组
     *   - meta: 分页元信息
     *     - total: 总记录数
     *     - current_page: 当前页码
     *     - last_page: 最后一页页码
     *     - per_page: 每页数量
     * 
     * 数据流向：
     * 1. 前端调用：departmentApi.getDepartmentList({ keyword: '研发', page: 1 })
     * 2. 请求发送：GET /api/v1/departments?keyword=研发&page=1
     * 3. 后端处理：DepartmentController::index()
     * 4. 数据转换：DepartmentResource::collection()
     * 5. 返回前端：包含分页信息的部门列表
     * 
     * 使用示例：
     * ```typescript
     * // 基础查询
     * const result = await getDepartmentList({ page: 1 })
     * 
     * // 搜索查询
     * const result = await getDepartmentList({
     *   keyword: '研发',
     *   status: DepartmentStatus.ENABLED,
     *   page: 1,
     *   per_page: 20
     * })
     * 
     * // 查询某个部门的子部门
     * const result = await getDepartmentList({
     *   parent_id: 1,
     *   page: 1
     * })
     * ```
     */
    getDepartmentList(params?: DepartmentQueryParams): Promise<PaginationResult<Department>> {
      // 从 routes.ts 引用 URL：/api/v1/departments
      return client.get<PaginationResult<Department>>(API_ROUTES.department.list, { params })
    },

    /**
     * 获取所有部门（不分页）
     * 
     * 使用场景：
     * - 在下拉选择器中使用（选择上级部门）
     * - 在员工表单中选择所属部门
     * - 需要一次性获取所有部门的场景
     * - 构建部门选择树的场景
     * 
     * @returns Promise<Department[]>
     *   - 返回所有启用的部门列表（不分页）
     * 
     * 数据流向：
     * 1. 前端调用：departmentApi.getAllDepartments()
     * 2. 请求发送：GET /api/v1/departments/all
     * 3. 后端处理：DepartmentController::all()
     * 4. 数据转换：DepartmentResource::collection()
     * 5. 返回前端：所有启用的部门列表
     * 
     * 使用示例：
     * ```typescript
     * // 获取所有部门用于下拉选择
     * const allDepartments = await getAllDepartments()
     * 
     * // 构建选项列表
     * const options = allDepartments.map(dept => ({
     *   label: dept.name,
     *   value: dept.id
     * }))
     * ```
     */
    getAllDepartments(): Promise<Department[]> {
      // 从 routes.ts 引用 URL：/api/v1/departments/all
      return client.get<Department[]>(API_ROUTES.department.all)
    },

    /**
     * 获取部门树形结构
     * 
     * 使用场景：
     * - 显示完整的部门组织架构树
     * - 在树形选择器中使用
     * - 展示部门层级关系
     * - 部门管理页面的树形视图
     * 
     * @returns Promise<Department[]>
     *   - 返回树形结构的部门列表
     *   - 每个部门包含 children 属性（子部门数组）
     *   - 根节点是没有 parent_id 的部门
     * 
     * 数据流向：
     * 1. 前端调用：departmentApi.getDepartmentTree()
     * 2. 请求发送：GET /api/v1/departments/tree
     * 3. 后端处理：DepartmentController::tree()
     * 4. 业务处理：DepartmentService::getTree() - 构建树形结构
     * 5. 数据转换：DepartmentResource::collection() - 包含 children 关联
     * 6. 返回前端：树形结构的部门列表
     * 
     * 返回数据结构示例：
     * ```typescript
     * [
     *   {
     *     id: 1,
     *     name: '总公司',
     *     parentId: null,
     *     children: [
     *       {
     *         id: 2,
     *         name: '研发部',
     *         parentId: 1,
     *         children: [
     *           { id: 3, name: '前端组', parentId: 2, children: [] },
     *           { id: 4, name: '后端组', parentId: 2, children: [] }
     *         ]
     *       },
     *       {
     *         id: 5,
     *         name: '生产部',
     *         parentId: 1,
     *         children: []
     *       }
     *     ]
     *   }
     * ]
     * ```
     * 
     * 使用示例：
     * ```typescript
     * // 获取部门树
     * const tree = await getDepartmentTree()
     * 
     * // 在树形组件中使用
     * <Tree data={tree} fieldNames={{ key: 'id', title: 'name' }} />
     * ```
     */
    getDepartmentTree(): Promise<Department[]> {
      // 从 routes.ts 引用 URL：/api/v1/departments/tree
      return client.get<Department[]>(API_ROUTES.department.tree)
    },

    /**
     * 获取部门统计信息
     * 
     * 使用场景：
     * - 在仪表板/控制台显示部门统计
     * - 在部门管理页面顶部显示统计卡片
     * - 监控部门数量和同步状态
     * 
     * @returns Promise<DepartmentStatistics>
     *   - total: number - 部门总数
     *   - enabled: number - 启用的部门数
     *   - disabled: number - 禁用的部门数
     *   - syncedToday: number - 今日同步的部门数
     * 
     * 数据流向：
     * 1. 前端调用：departmentApi.getDepartmentStatistics()
     * 2. 请求发送：GET /api/v1/departments/statistics
     * 3. 后端处理：DepartmentController::statistics()
     * 4. 业务处理：DepartmentService::getStatistics()
     * 5. 返回前端：统计数据对象
     * 
     * 使用示例：
     * ```typescript
     * // 获取统计数据
     * const stats = await getDepartmentStatistics()
     * console.log(`总部门数: ${stats.total}`)
     * console.log(`启用数: ${stats.enabled}`)
     * console.log(`禁用数: ${stats.disabled}`)
     * console.log(`今日同步: ${stats.syncedToday}`)
     * 
     * // 在卡片中显示
     * <Card title="部门统计">
     *   <Statistic title="总数" value={stats.total} />
     *   <Statistic title="启用" value={stats.enabled} />
     * </Card>
     * ```
     */
    getDepartmentStatistics(): Promise<DepartmentStatistics> {
      // 从 routes.ts 引用 URL：/api/v1/departments/statistics
      return client.get<DepartmentStatistics>(API_ROUTES.department.statistics)
    },

    /**
     * 获取部门详情
     * 
     * 使用场景：
     * - 在部门详情页查看完整信息
     * - 在编辑表单中回显数据
     * - 查看部门的上下级关系
     * 
     * @param id - 部门ID
     *   - 类型：number | string
     *   - 示例：1、'1'、'abc123'
     * 
     * @returns Promise<Department>
     *   - 返回部门的完整信息
     *   - 包含基础字段
     *   - 可能包含关联数据（parent、children）
     * 
     * 数据流向：
     * 1. 前端调用：departmentApi.getDepartmentDetail(1)
     * 2. 请求发送：GET /api/v1/departments/1
     * 3. 后端处理：DepartmentController::show(1)
     * 4. 业务处理：DepartmentService::find(1)
     * 5. 数据转换：DepartmentResource::toArray()
     * 6. 返回前端：部门详细信息
     * 
     * 使用示例：
     * ```typescript
     * // 获取ID为1的部门详情
     * const department = await getDepartmentDetail(1)
     * console.log(department.name)
     * console.log(department.fullPath) // 显示完整路径：总公司 / 研发部
     * 
     * // 在表单中回显
     * form.setFieldsValue({
     *   name: department.name,
     *   parent_id: department.parentId,
     *   order: department.order
     * })
     * ```
     */
    getDepartmentDetail(id: number | string): Promise<Department> {
      // 从 routes.ts 引用 URL：/api/v1/departments/{id}
      return client.get<Department>(API_ROUTES.department.detail(id))
    },

    /**
     * 创建部门
     * 
     * 使用场景：
     * - 在部门管理页面点击"新增"按钮
     * - 提交新增部门表单
     * - 批量导入部门数据时
     * 
     * @param data - 创建表单数据（CreateDepartmentForm）
     *   - name: string（必填）- 部门名称
     *     示例：'研发部'、'生产车间'
     *   - name_en?: string - 部门英文名称
     *     示例：'R&D Department'
     *   - parent_id?: number - 上级部门ID
     *     示例：1（表示在ID为1的部门下创建子部门）
     *   - wework_department_id?: string - 企业微信部门ID
     *     示例：'dept_001'
     *   - parent_wework_id?: string - 上级企业微信部门ID
     *   - order?: number - 排序值（越小越靠前）
     *     示例：10、20、30
     *   - status: DepartmentStatus（必填）- 状态
     *     示例：DepartmentStatus.ENABLED
     * 
     * @returns Promise<Department>
     *   - 返回新创建的部门信息（包含自动生成的 ID）
     * 
     * 数据流向：
     * 1. 前端调用：departmentApi.createDepartment({ name: '研发部', ... })
     * 2. 请求发送：POST /api/v1/departments
     *    请求体：{ name: '研发部', parent_id: 1, status: 1 }
     * 3. 后端处理：DepartmentController::store()
     * 4. 表单验证：CreateDepartmentRequest::rules()
     * 5. 业务处理：DepartmentService::create()
     * 6. 审计日志：AuditService::logCreate()
     * 7. 数据转换：DepartmentResource::toArray()
     * 8. 返回前端：新创建的部门信息
     * 
     * 使用示例：
     * ```typescript
     * // 创建一级部门
     * const newDept = await createDepartment({
     *   name: '研发部',
     *   name_en: 'R&D Department',
     *   order: 10,
     *   status: DepartmentStatus.ENABLED
     * })
     * 
     * // 创建子部门
     * const childDept = await createDepartment({
     *   name: '前端组',
     *   parent_id: newDept.id,
     *   order: 10,
     *   status: DepartmentStatus.ENABLED
     * })
     * 
     * message.success(`部门 ${newDept.name} 创建成功`)
     * ```
     */
    createDepartment(data: CreateDepartmentForm): Promise<Department> {
      // 从 routes.ts 引用 URL：/api/v1/departments
      return client.post<Department>(API_ROUTES.department.create, data)
    },

    /**
     * 更新部门
     * 
     * 使用场景：
     * - 在部门编辑页面提交修改
     * - 修改部门基本信息
     * - 调整部门层级关系
     * - 修改部门状态（启用/禁用）
     * 
     * @param id - 部门ID
     *   - 类型：number | string
     *   - 示例：1、'1'
     * 
     * @param data - 更新表单数据（UpdateDepartmentForm）
     *   - name?: string - 部门名称
     *   - name_en?: string - 部门英文名称
     *   - parent_id?: number - 上级部门ID（可以调整层级）
     *   - wework_department_id?: string - 企业微信部门ID
     *   - parent_wework_id?: string - 上级企业微信部门ID
     *   - order?: number - 排序值
     *   - status?: DepartmentStatus - 状态
     *   注意：所有字段都是可选的，只传需要修改的字段
     * 
     * @returns Promise<Department>
     *   - 返回更新后的部门信息
     * 
     * 数据流向：
     * 1. 前端调用：departmentApi.updateDepartment(1, { name: '研发中心' })
     * 2. 请求发送：PUT /api/v1/departments/1
     *    请求体：{ name: '研发中心' }
     * 3. 后端处理：DepartmentController::update(1)
     * 4. 表单验证：UpdateDepartmentRequest::rules()
     * 5. 业务处理：DepartmentService::update()
     * 6. 审计日志：AuditService::logUpdate()
     * 7. 数据转换：DepartmentResource::toArray()
     * 8. 返回前端：更新后的部门信息
     * 
     * 使用示例：
     * ```typescript
     * // 修改部门名称
     * const updated = await updateDepartment(1, {
     *   name: '研发中心'
     * })
     * 
     * // 调整部门层级
     * const updated = await updateDepartment(2, {
     *   parent_id: 3  // 将部门2移到部门3下
     * })
     * 
     * // 禁用部门
     * const updated = await updateDepartment(1, {
     *   status: DepartmentStatus.DISABLED
     * })
     * 
     * message.success('部门更新成功')
     * ```
     */
    updateDepartment(id: number | string, data: UpdateDepartmentForm): Promise<Department> {
      // 从 routes.ts 引用 URL：/api/v1/departments/{id}
      return client.put<Department>(API_ROUTES.department.departments(id), data)
    },

    /**
     * 删除部门
     * 
     * 使用场景：
     * - 在部门列表中删除不需要的部门
     * - 清理测试数据
     * 
     * @param id - 部门ID
     *   - 类型：number | string
     *   - 示例：1、'1'
     * 
     * @returns Promise<void>
     *   - 删除成功不返回数据
     * 
     * 数据流向：
     * 1. 前端调用：departmentApi.deleteDepartment(1)
     * 2. 请求发送：DELETE /api/v1/departments/1
     * 3. 后端处理：DepartmentController::destroy(1)
     * 4. 业务处理：DepartmentService::delete()
     *    - 检查是否有子部门（有则不能删除）
     *    - 检查是否有员工（有则不能删除）
     *    - 执行软删除
     * 5. 审计日志：AuditService::logDelete()
     * 6. 返回前端：删除成功
     * 
     * 约束条件：
     * - 部门下有子部门时无法删除（后端会返回错误）
     * - 部门下有员工时无法删除（后端会返回错误）
     * - 只能删除空的、叶子节点的部门
     * 
     * 使用示例：
     * ```typescript
     * // 删除部门（需要确认）
     * Modal.confirm({
     *   title: '确认删除',
     *   content: '删除后无法恢复，是否继续？',
     *   onOk: async () => {
     *     await deleteDepartment(1)
     *     message.success('删除成功')
     *     refreshList()
     *   }
     * })
     * 
     * // 批量删除（逐个调用）
     * for (const id of selectedIds) {
     *   try {
     *     await deleteDepartment(id)
     *   } catch (error) {
     *     console.error(`删除部门 ${id} 失败`, error)
     *   }
     * }
     * ```
     */
    deleteDepartment(id: number | string): Promise<void> {
      // 从 routes.ts 引用 URL：/api/v1/departments/{id}
      return client.delete<void>(API_ROUTES.department.delete(id))
    },

    // ===== 特殊功能 =====

    /**
     * 同步企业微信部门和员工数据
     * 
     * 使用场景：
     * - 初次接入企业微信时，批量导入部门和员工
     * - 定期同步企业微信的组织架构变更
     * - 员工入职/离职后同步最新数据
     * 
     * @returns Promise<WeworkSyncResult>
     *   - departments: 部门同步结果
     *     - created: number - 创建的部门数
     *     - updated: number - 更新的部门数
     *   - employees: 员工同步结果
     *     - created: number - 创建的员工数
     *     - updated: number - 更新的员工数
     *     - accountsCreated: number - 创建的系统账号数
     * 
     * 数据流向：
     * 1. 前端调用：departmentApi.syncWeworkDepartments()
     * 2. 请求发送：POST /api/v1/departments/sync-wework
     * 3. 后端处理：DepartmentController::syncWework()
     * 4. 业务处理：
     *    - WeworkSyncService::syncAll() - 全量同步
     *    - 调用企业微信API获取部门列表
     *    - DepartmentService::syncFromWework() - 批量创建/更新部门
     *    - EmployeeService::syncFromWework() - 批量创建/更新员工
     *    - UserService::createForEmployee() - 为新员工创建系统账号
     * 5. 审计日志：记录同步操作
     * 6. 返回前端：同步结果统计
     * 
     * 功能说明：
     * 1. 同步部门结构
     *    - 从企业微信获取所有部门
     *    - 根据 wework_department_id 判断是创建还是更新
     *    - 自动建立部门层级关系
     * 
     * 2. 同步员工信息
     *    - 从企业微信获取所有员工
     *    - 更新员工的部门归属
     *    - 更新员工的基本信息（姓名、手机号等）
     * 
     * 3. 自动创建员工档案
     *    - 如果员工在系统中不存在，则自动创建档案
     *    - 填充企业微信的基础信息
     * 
     * 4. 自动创建系统账号
     *    - 为新员工自动创建登录账号
     *    - 账号名使用企业微信的用户ID
     *    - 设置默认密码（如123456）
     *    - 分配默认角色
     * 
     * 使用示例：
     * ```typescript
     * // 执行同步（显示加载状态）
     * const loading = message.loading('正在同步企业微信数据...')
     * 
     * try {
     *   const result = await syncWeworkDepartments()
     *   loading()
     *   
     *   // 显示同步结果
     *   Modal.success({
     *     title: '同步完成',
     *     content: `
     *       部门：创建 ${result.departments.created} 个，更新 ${result.departments.updated} 个
     *       员工：创建 ${result.employees.created} 个，更新 ${result.employees.updated} 个
     *       账号：创建 ${result.employees.accountsCreated} 个
     *     `
     *   })
     *   
     *   // 刷新列表
     *   refreshDepartmentList()
     *   refreshEmployeeList()
     * } catch (error) {
     *   loading()
     *   message.error(`同步失败: ${error.message}`)
     * }
     * ```
     * 
     * 注意事项：
     * - 同步操作可能耗时较长（取决于部门和员工数量）
     * - 建议在低峰期执行
     * - 同步过程不会删除已有数据，只会创建和更新
     * - 如果企业微信中删除了部门/员工，系统中不会自动删除
     */
    syncWeworkDepartments(): Promise<WeworkSyncResult> {
      // 从 routes.ts 引用 URL：/api/v1/departments/sync-wework
      return client.post<WeworkSyncResult>(API_ROUTES.department.syncWework)
    },
  }
}

/**
 * ============================================================================
 * 🎯 使用提示
 * ============================================================================
 * 
 * 1. **推荐使用场景**：
 *    - ✅ Web + App 多端项目（共用一套 API 定义）
 *    - ✅ 需要单元测试的项目（可以注入 Mock 客户端）
 *    - ✅ 需要统一管理 API 的大型项目
 * 
 * 2. **不推荐使用场景**：
 *    - ❌ 单端项目（可以直接使用模板文件）
 *    - ❌ 需要深度定制请求逻辑的项目
 * 
 * 3. **核心优势**：
 *    - ✨ 类型安全：所有 API 都有完整的 TypeScript 类型
 *    - ✨ 注释详细：每个方法都有详细的中文注释
 *    - ✨ 可测试：可以注入 Mock 客户端进行单元测试
 *    - ✨ 可复用：Web 和 App 端共用同一套代码
 * 
 * 4. **性能考虑**：
 *    - 工厂函数只在初始化时执行一次
 *    - 生成的 API 方法会被缓存
 *    - 对性能影响可以忽略不计
 * 
 * ============================================================================
 */

